import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  CalendarDays,
  MapPin,
  Globe,
  Wrench,
  PackageOpen,
  ChevronLeft,
  ChevronRight,
  List,
  LayoutGrid,
  Check,
  ChevronsUpDown,
  FolderKanban,
  Timer,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { flagOf } from "../../lib/countryFlag";
import { UN_COUNTRIES } from "../../lib/unCountries";
import { useAuthStore } from "../../store/authStore";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../components/ui/command";

const EMPTY = {
  name: "",
  country: "",
  city: "",
  venue: "",
  start_date: "",
  end_date: "",
  setup_date: "",
  teardown_days: "",
  contract_date: "",
};

const fmt = (d) => {
  if (!d) return null;
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
};

const addDays = (dateStr, days) => {
  if (!dateStr || days === "" || days === null || days === undefined) return null;
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + Number(days));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

function CountryCombobox({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase("tr");
    if (!q) return UN_COUNTRIES;
    const starts = [];
    const contains = [];
    for (const c of UN_COUNTRIES) {
      const n = c.toLocaleLowerCase("tr");
      if (n.startsWith(q)) starts.push(c);
      else if (n.includes(q)) contains.push(c);
    }
    return [...starts, ...contains];
  }, [query]);
  return (
    <Popover modal open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          className="mt-1 flex h-9 w-full items-center justify-between rounded-md border border-zinc-200 bg-white px-3 text-sm transition-colors hover:border-zinc-300 focus:shadow-none focus-visible:shadow-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-zinc-400"
          data-testid="fair-country-select"
        >
          <span className={`truncate ${value ? "text-zinc-900" : "text-zinc-400"}`}>
            {value || "Ülke seç"}
          </span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0"
        style={{ width: "var(--radix-popover-trigger-width)" }}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Ülke ara..."
            value={query}
            onValueChange={setQuery}
            className="h-8 text-sm focus:shadow-none focus-visible:shadow-none"
            data-testid="fair-country-search"
          />
          <CommandList className="max-h-52 overscroll-contain">
            <CommandEmpty className="py-4 text-center text-xs text-zinc-400">
              Sonuç bulunamadı
            </CommandEmpty>
            {filtered.map((c) => (
              <CommandItem
                key={c}
                value={c}
                className="py-1 text-[13px]"
                onSelect={() => { onChange(c); setOpen(false); setQuery(""); }}
              >
                <Check className={`mr-2 h-3.5 w-3.5 ${value === c ? "opacity-100" : "opacity-0"}`} />
                {c}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function FairDialog({ open, onClose, initial, fairs, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open)
      setForm({
        ...EMPTY,
        ...(Object.fromEntries(
          Object.entries(initial || {}).filter(([k]) => k in EMPTY),
        )),
        teardown_days: initial?.teardown_days ?? "",
      });
  }, [open, initial]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // City memory: previously used cities for the selected country → datalist
  // suggestions (prevents "dubai / Dubai / Dubay" duplicates).
  const citySuggestions = useMemo(() => {
    const seen = new Map();
    (fairs || []).forEach((f) => {
      if (!f.city) return;
      if (form.country && f.country !== form.country) return;
      const key = f.city.trim().toLocaleLowerCase("tr");
      if (key && !seen.has(key)) seen.set(key, f.city.trim());
    });
    return [...seen.values()].sort((a, b) => a.localeCompare(b, "tr"));
  }, [fairs, form.country]);

  const teardownDate = addDays(form.end_date, form.teardown_days);
  const setupEndDate = addDays(form.start_date, -1);

  const save = async () => {
    if (!form.name.trim()) return;
    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      toast.error("Bitiş tarihi başlangıç tarihinden önce olamaz");
      return;
    }
    if (form.start_date && form.setup_date && form.setup_date > form.start_date) {
      toast.error("Kurulum tarihi başlangıç tarihinden sonra olamaz");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        country: form.country.trim(),
        city: form.city.trim(),
        venue: form.venue.trim(),
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        setup_date: form.setup_date || null,
        teardown_days: form.teardown_days === "" ? null : Number(form.teardown_days),
        contract_date: form.contract_date || null,
      };
      if (initial?.id) await api.patch(`/expo/fairs/${initial.id}`, payload);
      else await api.post("/expo/fairs", payload);
      toast.success(initial?.id ? "Fuar güncellendi" : "Fuar eklendi");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="fair-dialog">
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Fuarı düzenle" : "Yeni fuar"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Fuar adı *</Label>
            <Input value={form.name} onChange={set("name")} className="mt-1" data-testid="fair-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Ülke</Label>
              <CountryCombobox
                value={form.country}
                onChange={(c) => setForm((f) => ({ ...f, country: c }))}
              />
            </div>
            <div>
              <Label>Şehir</Label>
              <Input
                value={form.city}
                onChange={set("city")}
                list="fair-city-suggestions"
                className="mt-1"
                data-testid="fair-city-input"
              />
              <datalist id="fair-city-suggestions">
                {citySuggestions.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>
          </div>
          <div>
            <Label>Mekan (venue)</Label>
            <Input value={form.venue} onChange={set("venue")} className="mt-1" data-testid="fair-venue-input" />
          </div>
          <div>
            <Label>Sözleşme Tarihi</Label>
            <Input type="date" value={form.contract_date || ""} onChange={set("contract_date")} className="mt-1" data-testid="fair-contract-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Başlangıç *</Label>
              <Input type="date" value={form.start_date || ""} onChange={set("start_date")} className="mt-1" data-testid="fair-start-input" />
            </div>
            <div>
              <Label>Bitiş</Label>
              <Input
                type="date"
                min={form.start_date || undefined}
                value={form.end_date || ""}
                onChange={set("end_date")}
                className="mt-1"
                data-testid="fair-end-input"
              />
            </div>
            <div>
              <Label>Kurulum</Label>
              <Input
                type="date"
                max={form.start_date || undefined}
                value={form.setup_date || ""}
                onChange={set("setup_date")}
                className="mt-1"
                data-testid="fair-setup-input"
              />
            </div>
            <div>
              <Label>Kurulum Bitiş</Label>
              <Input
                type="date"
                value={setupEndDate || ""}
                disabled
                readOnly
                className="mt-1 bg-zinc-50 text-zinc-500"
                data-testid="fair-setup-end-input"
              />
              <p className="mt-1 text-[11px] text-zinc-400">
                Otomatik — fuar başlangıcından 1 gün önce.
              </p>
            </div>
            <div>
              <Label>Söküm süresi (gün)</Label>
              <Input
                type="number"
                min="0"
                value={form.teardown_days}
                onChange={set("teardown_days")}
                placeholder="örn. 2"
                className="mt-1"
                data-testid="fair-teardown-days-input"
              />
            </div>
          </div>
          <p className="rounded-md bg-zinc-50 px-2.5 py-1.5 text-[11px] text-zinc-500" data-testid="fair-teardown-info">
            Söküm, fuar bitiş günü başlar.
            {teardownDate
              ? ` Söküm son tarihi: ${fmt(teardownDate)}`
              : " Bitiş tarihi ve söküm süresi girilirse son tarih otomatik hesaplanır."}
          </p>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose} data-testid="fair-cancel-btn">Vazgeç</Button>
            <Button onClick={save} disabled={saving || !form.name.trim()} data-testid="fair-save-btn">
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              Kaydet
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FairCard({ fair, isAdmin, onEdit, onDelete }) {
  const past = fair.end_date
    ? new Date(`${fair.end_date}T23:59:59`) < new Date()
    : fair.start_date
      ? new Date(`${fair.start_date}T23:59:59`) < new Date()
      : false;

  // Countdown: days until the fair starts (local midnight based).
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dLeft = fair.start_date
    ? Math.round((new Date(`${fair.start_date}T00:00:00`) - today) / 86400000)
    : null;
  const ongoing = !past && dLeft !== null && dLeft <= 0;
  const accent = past
    ? "border-zinc-100 opacity-70"
    : ongoing
      ? "border-emerald-300 ring-1 ring-emerald-100"
      : dLeft !== null && dLeft <= 7
        ? "border-red-200 ring-1 ring-red-100"
        : dLeft !== null && dLeft <= 30
          ? "border-amber-200"
          : "border-zinc-200";
  const countdown = past
    ? null
    : ongoing
      ? { label: "Fuar devam ediyor", cls: "bg-emerald-600 text-white" }
      : dLeft === null
        ? null
        : dLeft === 0
          ? { label: "Bugün başlıyor", cls: "bg-red-600 text-white animate-pulse" }
          : dLeft === 1
            ? { label: "Yarın başlıyor", cls: "bg-red-600 text-white" }
            : dLeft <= 7
              ? { label: `${dLeft} gün kaldı`, cls: "bg-red-100 text-red-700" }
              : dLeft <= 30
                ? { label: `${dLeft} gün kaldı`, cls: "bg-amber-100 text-amber-800" }
                : { label: `${dLeft} gün kaldı`, cls: "bg-zinc-100 text-zinc-600" };

  return (
    <div
      className={`group rounded-xl border bg-white p-4 shadow-sm transition-shadow hover:shadow-md ${accent}`}
      data-testid={`fair-card-${fair.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate font-semibold text-zinc-900" data-testid={`fair-name-${fair.id}`}>
              {flagOf(fair.country) ? `${flagOf(fair.country)} ` : ""}
              {fair.name}
            </h3>
            {countdown && (
              <span
                data-testid={`fair-countdown-${fair.id}`}
                className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${countdown.cls}`}
              >
                <Timer className="h-3 w-3" />
                {countdown.label}
              </span>
            )}
          </div>
          <p className="mt-0.5 flex items-center gap-1 text-sm text-zinc-500">
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            {[fair.city, fair.country].filter(Boolean).join(", ") || "—"}
            {fair.venue ? ` · ${fair.venue}` : ""}
          </p>
        </div>
        {isAdmin && (
          <div className="flex shrink-0 gap-1 opacity-0 transition-opacity group-hover:opacity-100">
            <button
              type="button"
              onClick={onEdit}
              className="rounded p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
              data-testid={`fair-edit-${fair.id}`}
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="rounded p-1.5 text-zinc-400 hover:bg-red-50 hover:text-red-600"
              data-testid={`fair-delete-${fair.id}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-zinc-600">
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 font-medium text-blue-700">
          <CalendarDays className="h-3 w-3" />
          {fmt(fair.start_date) || "?"}{fair.end_date ? ` – ${fmt(fair.end_date)}` : ""}
        </span>
        {fair.setup_date && (
          <span className="inline-flex items-center gap-1 text-zinc-500">
            <Wrench className="h-3 w-3" /> Kurulum: {fmt(fair.setup_date)}
          </span>
        )}
        {fair.teardown_date && (
          <span className="inline-flex items-center gap-1 text-zinc-500">
            <PackageOpen className="h-3 w-3" /> Söküm son: {fmt(fair.teardown_date)}
          </span>
        )}
        {fair.website && (
          <a
            href={fair.website.startsWith("http") ? fair.website : `https://${fair.website}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-blue-600 hover:underline"
            data-testid={`fair-website-${fair.id}`}
          >
            <Globe className="h-3 w-3" /> Web sitesi
          </a>
        )}
      </div>
      {(fair.projects || []).length > 0 && (
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5" data-testid={`fair-projects-${fair.id}`}>
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-zinc-400">
            <FolderKanban className="h-3 w-3" /> Projeler:
          </span>
          {fair.projects.map((p) => (
            <Link
              key={p.id}
              to={`/app/projects/${p.id}`}
              className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 hover:bg-emerald-100"
              data-testid={`fair-project-chip-${p.id}`}
            >
              {p.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

const PALETTE = [
  "bg-blue-100 text-blue-800",
  "bg-emerald-100 text-emerald-800",
  "bg-violet-100 text-violet-800",
  "bg-rose-100 text-rose-800",
  "bg-cyan-100 text-cyan-800",
  "bg-orange-100 text-orange-800",
];
const colorOf = (id) => {
  let h = 0;
  for (const ch of id || "") h = (h * 31 + ch.charCodeAt(0)) % 997;
  return PALETTE[h % PALETTE.length];
};

const iso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function FairCalendar({ fairs, onFairClick }) {
  const [month, setMonth] = useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });
  const todayIso = iso(new Date());
  const startOffset = (month.getDay() + 6) % 7; // Monday-start
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++)
    cells.push(new Date(month.getFullYear(), month.getMonth(), d));
  while (cells.length % 7 !== 0) cells.push(null);

  const fairsOn = (dayIso) =>
    fairs.filter((f) => {
      if (!f.start_date) return false;
      const end = f.end_date || f.start_date;
      return f.start_date <= dayIso && dayIso <= end;
    });

  const monthLabel = month.toLocaleDateString("tr-TR", { month: "long", year: "numeric" });

  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm" data-testid="fair-calendar">
      <div className="mb-3 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
          className="rounded-lg p-1.5 text-zinc-500 hover:bg-zinc-100"
          data-testid="calendar-prev-month"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <h2 className="text-sm font-semibold capitalize text-zinc-900" data-testid="calendar-month-label">
          {monthLabel}
        </h2>
        <button
          type="button"
          onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
          className="rounded-lg p-1.5 text-zinc-500 hover:bg-zinc-100"
          data-testid="calendar-next-month"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-px text-center text-[11px] font-medium uppercase tracking-wider text-zinc-400">
        {["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"].map((d) => (
          <div key={d} className="pb-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px overflow-hidden rounded-lg bg-zinc-200">
        {cells.map((day, i) => {
          if (!day) return <div key={i} className="min-h-[76px] bg-zinc-50" />;
          const dIso = iso(day);
          const dayFairs = fairsOn(dIso);
          const setups = fairs.filter((f) => f.setup_date === dIso);
          const teardowns = fairs.filter((f) => f.teardown_date === dIso);
          const isToday = dIso === todayIso;
          return (
            <div key={i} className="min-h-[76px] bg-white p-1" data-testid={`calendar-day-${dIso}`}>
              <span
                className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] ${
                  isToday ? "bg-zinc-900 font-semibold text-white" : "text-zinc-500"
                }`}
              >
                {day.getDate()}
              </span>
              <div className="mt-0.5 space-y-0.5">
                {dayFairs.slice(0, 2).map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => onFairClick(f)}
                    title={`${f.name}${f.venue ? ` · ${f.venue}` : ""}`}
                    className={`block w-full truncate rounded px-1 py-0.5 text-left text-[10px] font-medium leading-tight ${colorOf(f.id)}`}
                    data-testid={`calendar-fair-${f.id}-${dIso}`}
                  >
                    {f.start_date === dIso ? "▸ " : ""}{f.name}
                  </button>
                ))}
                {dayFairs.length > 2 && (
                  <span className="block px-1 text-[10px] text-zinc-400">+{dayFairs.length - 2}</span>
                )}
                {setups.map((f) => (
                  <span
                    key={`s-${f.id}`}
                    title={`Kurulum: ${f.name}`}
                    className="block w-full truncate rounded border border-amber-200 bg-amber-50 px-1 py-0.5 text-[10px] leading-tight text-amber-700"
                  >
                    🔧 {f.name}
                  </span>
                ))}
                {teardowns.map((f) => (
                  <span
                    key={`t-${f.id}`}
                    title={`Söküm son: ${f.name}`}
                    className="block w-full truncate rounded border border-violet-200 bg-violet-50 px-1 py-0.5 text-[10px] leading-tight text-violet-700"
                  >
                    📦 {f.name}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ExpoFairs() {
  const { user } = useAuthStore();
  const isAdmin = ["super_admin", "ultra_admin"].includes(user?.role);
  const [fairs, setFairs] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [view, setView] = useState("list");

  const load = async () => {
    const { data } = await api.get("/expo/fairs");
    setFairs(data);
  };
  useEffect(() => {
    load().catch(() => toast.error("Fuarlar yüklenemedi"));
  }, []);

  const remove = async (fair) => {
    try {
      await api.delete(`/expo/fairs/${fair.id}`);
      toast.success("Fuar silindi");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Silinemedi");
    }
  };

  if (!fairs) {
    return (
      <div className="flex items-center gap-2 p-8 text-sm text-zinc-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Fuarlar yükleniyor...
      </div>
    );
  }

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = fairs.filter((f) => !f.end_date || f.end_date >= today);
  const pastList = fairs.filter((f) => f.end_date && f.end_date < today).reverse();

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6" data-testid="expo-fairs-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold text-zinc-900">
            <CalendarDays className="h-5 w-5" /> Fuar Takvimi
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            Yaklaşan fuarlar, kurulum/söküm tarihleri ve bağlı projeler.
          </p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            data-testid="fair-new-btn"
          >
            <Plus className="mr-1.5 h-4 w-4" /> Yeni Fuar
          </Button>
        )}
      </div>

      <div className="flex items-center gap-1 rounded-lg border border-zinc-200 bg-white p-0.5 shadow-sm" style={{ width: "fit-content" }}>
        <button
          type="button"
          onClick={() => setView("list")}
          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${view === "list" ? "bg-zinc-900 text-white" : "text-zinc-500 hover:text-zinc-800"}`}
          data-testid="fairs-view-list"
        >
          <List className="h-3.5 w-3.5" /> Liste
        </button>
        <button
          type="button"
          onClick={() => setView("calendar")}
          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${view === "calendar" ? "bg-zinc-900 text-white" : "text-zinc-500 hover:text-zinc-800"}`}
          data-testid="fairs-view-calendar"
        >
          <LayoutGrid className="h-3.5 w-3.5" /> Takvim
        </button>
      </div>

      {view === "calendar" && (
        <FairCalendar
          fairs={fairs}
          onFairClick={(f) => {
            if (isAdmin) {
              setEditing(f);
              setDialogOpen(true);
            }
          }}
        />
      )}

      {view === "list" && fairs.length === 0 && (
        <div className="rounded-xl border border-dashed border-zinc-300 p-10 text-center text-sm text-zinc-500" data-testid="fairs-empty">
          Henüz fuar eklenmemiş.
        </div>
      )}

      {view === "list" && upcoming.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-zinc-400">
            Yaklaşan ({upcoming.length})
          </h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {upcoming.map((f) => (
              <FairCard
                key={f.id}
                fair={f}
                isAdmin={isAdmin}
                onEdit={() => { setEditing(f); setDialogOpen(true); }}
                onDelete={() => setDeleting(f)}
              />
            ))}
          </div>
        </div>
      )}

      {view === "list" && pastList.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-zinc-400">
            Geçmiş ({pastList.length})
          </h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {pastList.map((f) => (
              <FairCard
                key={f.id}
                fair={f}
                isAdmin={isAdmin}
                onEdit={() => { setEditing(f); setDialogOpen(true); }}
                onDelete={() => setDeleting(f)}
              />
            ))}
          </div>
        </div>
      )}

      <FairDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        initial={editing}
        fairs={fairs}
        onSaved={load}
      />

      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="fair-delete-dialog">
          <DialogHeader>
            <DialogTitle>Fuarı sil</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-600">
            "{deleting?.name}" silinecek. Bu fuara bağlı projeler fuar bağlantısını
            kaybeder. Emin misiniz?
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleting(null)}>Vazgeç</Button>
            <Button variant="destructive" onClick={() => remove(deleting)} data-testid="fair-delete-confirm">
              Sil
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
